import React, { useState } from 'react';
import { AppNav } from '../../components/layout/AppNav';
import { ChevronDown, Mail, Phone, HelpCircle, Search } from 'lucide-react';

const FAQS = [
  { q: 'How do I create a new offer?', a: 'Click "New Offer" in the sidebar to open the product catalog. Select a product to start an offer, then follow the wizard steps: Location (if required), Cart, Customer Info, Authorized Person, and Summary.' },
  { q: 'How does coverage check work?', a: 'For 5G and Fixed products, you must pin the delivery location on the map. The system checks whether the address falls within the service coverage radius.' },
  { q: 'What happens after I send an offer?', a: 'The customer receives an SMS or email with a secure link to view and accept or reject the offer. The link expires after 7 days.' },
  { q: 'Can I edit an offer after saving it?', a: 'Yes. Find the offer in "My Offers", open it, and click "Continue Editing". You will be taken to the last incomplete step.' },
  { q: 'What is the VAT rate?', a: 'All displayed prices are VAT-inclusive at 15%. The VAT breakdown is shown in the cart and offer summary.' },
  { q: 'How do I add multiple products to one offer?', a: 'After adding your first product to the cart, click "Add Another Product" on the Cart page to return to the catalog and add more items to the same offer.' },
  { q: 'What formats are accepted for the CR Number?', a: 'The Commercial Registration (CR) number must be exactly 10 digits.' },
  { q: 'What formats are accepted for the VAT Number?', a: 'The VAT number must be exactly 15 digits, starting and ending with the digit 3.' },
  { q: 'What documents are required for the offer?', a: 'You need to upload: Customer CR (Commercial Registration), Customer VAT Certificate, National Address Document, and Authorized Person Letter. These are uploaded in the relevant wizard steps.' },
  { q: 'How do I contact support?', a: 'Use the contact details in the Support section below.' },
];

export const HelpPage: React.FC = () => {
  const [open, setOpen] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Help & Support" subtitle="Frequently asked questions and support" />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '720px', display: 'flex', flexDirection: 'column', gap: '24px' }}>

          <div className="page-title" style={{ marginBottom: '0' }}>Help & Support</div>

          {/* Search bar */}
          <div style={{ position: 'relative' }}>
            <Search size={15} color="var(--text-3)" style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="search"
              placeholder="Search FAQs…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOpen(null); }}
              className="inp"
              style={{ paddingLeft: 36 }}
            />
          </div>

          {/* FAQ */}
          <section>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <HelpCircle size={16} color="var(--p)" />
              <h2 className="section-title" style={{ marginBottom: 0 }}>Frequently Asked Questions</h2>
              {search && (
                <span style={{ fontSize: 12, color: 'var(--text-3)', marginLeft: 'auto' }}>
                  {FAQS.filter((f) => f.q.toLowerCase().includes(search.toLowerCase()) || f.a.toLowerCase().includes(search.toLowerCase())).length} result(s)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {FAQS.filter((faq) => !search || faq.q.toLowerCase().includes(search.toLowerCase()) || faq.a.toLowerCase().includes(search.toLowerCase())).map((faq, i) => (
                <div
                  key={i}
                  className="card"
                  style={{ border: open === i ? '1.5px solid var(--p)' : '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}
                >
                  <button
                    onClick={() => setOpen(open === i ? null : i)}
              key={faq.q}
                    style={{
                      width: '100%', padding: '14px 16px',
                      background: open === i ? 'var(--p-pale)' : 'transparent',
                      border: 'none', textAlign: 'left',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                  >
                    <span style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-1)', paddingRight: '12px' }}>{faq.q}</span>
                    <ChevronDown
                      size={16}
                      color="var(--p)"
                      style={{ transform: open === i ? 'rotate(180deg)' : 'none', transition: 'transform .2s', flexShrink: 0 }}
                    />
                  </button>
                  {open === i && (
                    <div style={{ padding: '0 16px 14px', fontSize: '13.5px', color: 'var(--text-1)', lineHeight: 1.65 }}>
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section>
            <h2 className="section-title" style={{ marginBottom: '14px' }}>Contact Support</h2>
            <div className="card">
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13.5px' }}>
                  <Mail size={15} color="var(--p)" />
                  <span style={{ color: 'var(--text-2)' }}>Email:</span>
                  <a href="mailto:rms-support@salam.sa" style={{ color: 'var(--p)', fontWeight: 600 }}>
                    rms-support@salam.sa
                  </a>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13.5px' }}>
                  <Phone size={15} color="var(--p)" />
                  <span style={{ color: 'var(--text-2)' }}>Phone:</span>
                  <strong style={{ color: 'var(--text-1)' }}>920004444</strong>
                  <span style={{ color: 'var(--text-2)' }}>(Salam Business Support)</span>
                </div>
                <div style={{ fontSize: '12.5px', color: 'var(--text-3)', paddingTop: '4px', borderTop: '1px solid var(--border)' }}>
                  Available Sunday – Thursday, 8:00 AM – 5:00 PM KSA time.
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
